'use client'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiInstance } from '@/apiInstance'
import { RoutePath } from '@/routes'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { VariableSelector, type VariableSelectorRef, type VariableValue } from './VariableSelector'
import type {
  VariableTypeInput,
  ComponentSummary,
  Game,
  BasicPersonaOutput,
} from '@/types/models/v2'
import { toast } from 'react-hot-toast'
import { avatars } from './playground-avatar/config/avatars'

export default function DemosPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [gameId, setGameId] = useState(searchParams.get('game_id') || '')
  const [componentId, setComponentId] = useState(searchParams.get('component_id') || '')
  const [avatarType, setAvatarType] = useState(searchParams.get('avatar') || 'robot')
  const [personaId, setPersonaId] = useState(searchParams.get('persona_id') || 'none')
  const [imageFrame, setImageFrame] = useState<string | null>(
    searchParams.get('imageFrame') || 'frame'
  )
  const [isEnableDebug, setIsEnableDebug] = useState(searchParams.get('isEnableDebug') === 'true')
  const [isEnableInterrupt, setIsEnableInterrupt] = useState(
    searchParams.get('isEnableInterrupt') !== 'false'
  )
  const [isEnabledSubtitles, setIsEnabledSubtitles] = useState(
    searchParams.get('isEnabledSubtitles') !== 'false'
  )
  const [isLoadingComponent, setIsLoadingComponent] = useState(false)
  const [isComponentLoaded, setIsComponentLoaded] = useState(false)
  const [availableVariables, setAvailableVariables] = useState<VariableTypeInput[]>([])
  const [components, setComponents] = useState<ComponentSummary[]>([])
  const [isLoadingGames, setIsLoadingGames] = useState(true)
  const [games, setGames] = useState<Game[]>([])
  const [personas, setPersonas] = useState<BasicPersonaOutput[]>([])
  const [selectedVariables, setSelectedVariables] = useState<VariableValue[]>([])

  const variableSelectorRef = useRef<VariableSelectorRef>(null)
  const navigate = useNavigate()

  // Handle variable changes
  const handleVariablesChange = (variables: VariableValue[]) => {
    // Just store the variables, no URL updates
    setSelectedVariables(variables)
  }

  if (!apiInstance.authToken) {
    navigate(RoutePath.LOGIN)
  }

  // Extract var_ parameters from URL
  const getInitialVariables = () => {
    const variables: { name: string; value: string }[] = []
    searchParams.forEach((value, key) => {
      if (key.startsWith('var_')) {
        variables.push({
          name: key.replace('var_', ''),
          value: value,
        })
      }
    })
    return variables
  }

  // Set initial variables when component loads
  useEffect(() => {
    if (variableSelectorRef.current && isComponentLoaded) {
      const initialVariables = getInitialVariables()
      if (initialVariables.length > 0) {
        variableSelectorRef.current.setSelectedVariables(initialVariables)
      }
    }
  }, [isComponentLoaded])

  // Update URL when selections change (except variables)
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (gameId) params.set('game_id', gameId)
    if (componentId) params.set('component_id', componentId)
    if (avatarType) params.set('avatar', avatarType)
    if (personaId !== 'none') params.set('persona_id', personaId)
    if (imageFrame) params.set('imageFrame', imageFrame)
    params.set('isEnableDebug', String(isEnableDebug))
    params.set('isEnableInterrupt', String(isEnableInterrupt))
    params.set('isEnabledSubtitles', String(isEnabledSubtitles))
    setSearchParams(params)
  }, [
    gameId,
    componentId,
    avatarType,
    personaId,
    imageFrame,
    isEnableDebug,
    isEnableInterrupt,
    isEnabledSubtitles,
  ])

  useEffect(() => {
    const loadGames = async () => {
      setIsLoadingGames(true)
      try {
        const response = await apiInstance.getGames()
        setGames(response.games)
        // If we have a game_id in URL, try to load it
        if (gameId) {
          const gameExists = response.games.some((g) => g.id === gameId)
          if (!gameExists) {
            toast.error('Game not found')
            setGameId('')
          }
        }
      } catch (error) {
        console.error('Failed to load games:', error)
        setGames([])
      } finally {
        setIsLoadingGames(false)
      }
    }

    loadGames()
  }, [])

  useEffect(() => {
    if (gameId) {
      loadComponents()
    }
  }, [gameId])

  const loadComponents = async () => {
    try {
      const response = await apiInstance.getAllComponents({ gameId })
      setComponents(response.components)
      // If we have a component_id in URL, try to load it
      if (componentId) {
        const componentExists = response.components.some((c) => c.id === componentId)
        if (!componentExists) {
          toast.error('Component not found')
          setComponentId('')
        } else {
          loadComponent(componentId)
        }
      }
    } catch (error) {
      console.error('Failed to load components:', error)
      setComponents([])
    }
  }

  const loadComponent = async (id: string) => {
    setIsLoadingComponent(true)
    try {
      const response = await apiInstance.getComponent(id)
      setIsComponentLoaded(true)
      if (response.variables) {
        setAvailableVariables(response.variables)
      }
      if (response.personas) {
        setPersonas(response.personas)
      }
    } catch (error) {
      console.error('Failed to load component:', error)
      setIsComponentLoaded(false)
      setAvailableVariables([])
      setPersonas([])
      setPersonaId('none')
    } finally {
      setIsLoadingComponent(false)
    }
  }

  const handleLaunch = (e: React.MouseEvent) => {
    e.preventDefault()

    if (!gameId || !componentId) {
      toast.error('Please select both a game and a component before launching')
      return
    }

    // Get current variables
    const variables = variableSelectorRef.current?.getSelectedVariables() || []

    // Construct the URL with all parameters
    const params = new URLSearchParams(searchParams)

    // Add variable parameters
    variables.forEach((variable) => {
      if (variable.name && variable.value !== undefined && variable.value !== null) {
        params.set(`var_${variable.name}`, String(variable.value))
      }
    })

    const playgroundPath = `/games/${gameId}/components/${componentId}/avatar-playground/`
    navigate(`${playgroundPath}?${params.toString()}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF9EC] to-white p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h1
            className="text-4xl font-bold mb-8 bg-gradient-to-r from-[#94D7D7] to-[#987FDD] bg-clip-text text-transparent cursor-default"
            onClick={(e) => e.preventDefault()}
          >
            Avatar Demos
          </h1>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Game</label>
                  <Select
                    value={gameId}
                    onValueChange={(value) => {
                      setGameId(value)
                      setComponentId('')
                      setIsComponentLoaded(false)
                      setAvailableVariables([])
                      setPersonas([])
                      setPersonaId('none')
                    }}
                  >
                    <SelectTrigger className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#94D7D7] focus:border-transparent transition-all">
                      <SelectValue
                        placeholder={isLoadingGames ? 'Loading games...' : 'Select a game'}
                      >
                        {isLoadingGames ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading games...</span>
                          </div>
                        ) : (
                          games.find((g) => g.id === gameId)?.name
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {games.map((game) => (
                        <SelectItem key={game.id} value={game.id || ''}>
                          {game.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Component</label>
                    {isComponentLoaded && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={componentId}
                      onValueChange={async (value) => {
                        setComponentId(value)
                        setIsComponentLoaded(false)
                        setAvailableVariables([])
                        setPersonas([])
                        setPersonaId('none')
                        await loadComponent(value)
                      }}
                    >
                      <SelectTrigger className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#94D7D7] focus:border-transparent transition-all">
                        <SelectValue placeholder="Select a component">
                          {isLoadingComponent ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Loading...</span>
                            </div>
                          ) : (
                            components.find((c) => c.id === componentId)?.name
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {components.map((component) => (
                          <SelectItem key={component.id} value={component.id}>
                            {component.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Persona</label>
                  <Select
                    value={personaId}
                    onValueChange={setPersonaId}
                    disabled={!isComponentLoaded || personas.length === 0}
                  >
                    <SelectTrigger className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#94D7D7] focus:border-transparent transition-all">
                      <SelectValue
                        placeholder={
                          personas.length === 0 ? 'No personas available' : 'Select a persona'
                        }
                      >
                        {personaId === 'none'
                          ? 'None'
                          : personas.find((p) => p.id === personaId)?.name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {personas.map((persona) => (
                        <SelectItem key={persona.id} value={persona.id}>
                          {persona.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isComponentLoaded && availableVariables.length > 0 && (
                  <VariableSelector
                    ref={variableSelectorRef}
                    availableVariables={availableVariables}
                    onVariablesChange={handleVariablesChange}
                  />
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Avatar Type</label>
                  <Select value={avatarType} onValueChange={setAvatarType}>
                    <SelectTrigger className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#94D7D7] focus:border-transparent transition-all">
                      <SelectValue placeholder="Select avatar" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(avatars).map(([key, avatar]) => (
                        <SelectItem key={key} value={key}>{avatar.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Image Frame</label>
                    <InfoTooltip content="This setting determines where generated images will be displayed in the experience" />
                  </div>
                  <Select
                    value={imageFrame || 'frame'}
                    onValueChange={(value) => setImageFrame(value === 'none' ? null : value)}
                  >
                    <SelectTrigger className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#94D7D7] focus:border-transparent transition-all">
                      <SelectValue placeholder="Select frame type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="frame">Frame</SelectItem>
                      <SelectItem value="fullscreen">Fullscreen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="debug"
                      checked={isEnableDebug}
                      onCheckedChange={(checked) => setIsEnableDebug(checked as boolean)}
                    />
                    <label htmlFor="debug" className="text-sm font-medium text-gray-700">
                      Enable Debug
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="interrupt"
                      checked={isEnableInterrupt}
                      onCheckedChange={(checked) => setIsEnableInterrupt(checked as boolean)}
                    />
                    <div className="flex items-center gap-2">
                      <label htmlFor="interrupt" className="text-sm font-medium text-gray-700">
                        Enable Interruptions
                      </label>
                      <InfoTooltip content="Set this flag off if you are in a very noisy environment like a classroom. Otherwise, this allows you to interrupt the avatar while she is talking" />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="subtitles"
                      checked={isEnabledSubtitles}
                      onCheckedChange={(checked) => setIsEnabledSubtitles(checked as boolean)}
                    />
                    <div className="flex items-center gap-2">
                      <label htmlFor="subtitles" className="text-sm font-medium text-gray-700">
                        Enable Subtitles
                      </label>
                      <InfoTooltip content="Enable or disable subtitles for the avatar's speech." />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleLaunch}
              className="inline-flex items-center justify-center w-full md:w-auto px-6 py-3 bg-gradient-to-r from-[#94D7D7] to-[#987FDD] text-white rounded-lg hover:opacity-90 transition-opacity shadow-md"
            >
              Launch Avatar Playground
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 ml-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
